import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {DataLoggerDefaults, Device, Register} from "../../model/device.model";
import {ModbusService} from "../../modbusrtu/modbus.service";
import moment from "moment";
import {AttributeID} from "../../model/device-attribute.model";

export enum MeterRegisters {
  baud,
  databit,
  stopbit,
  parity,
  readfreq,
  readcount1,
  readcount2,
  address,
  firstmeasure
}

@Component({
    selector: 'app-meter-overview',
    templateUrl: './meter-overview.component.html',
    standalone: false
})
export class MeterOverviewComponent implements OnInit, OnChanges {
  MAX_UINT = 65536;

  @Input() item: Device;
  @Input() deviceData:any = {};
  @Output() deviceDataChange = new EventEmitter<any>();

  result: any;
  queryData = [];
  ctRatioVisible = false;

  meterRegisterLabel = [
    'Baud rate',
    'Databit',
    'Stopbit',
    'Parity',
    'Reading frequency',
    'Reading count (1)',
    'Reading count (2)',
    'Address'
  ];

  constructor(
    private service: ModbusService
  ) {
  }

  ngOnInit(): void {
    if (this.item.isMeter()) {
      this.ctRatioVisible = this.item.isCTRatioVisible();
      this.extractSavedRegisters(this.item.registers)
    }
  }

  extractSavedRegisters(registers) {
    const index = this.item.getIndex();
    if (+index > -1 && registers) {
      const neededRegisters = [];
      const registerStart = (+index * 100);
      for (let i = 0; i < 8; i++) {
        let reg = registerStart + i;
        neededRegisters[reg] = registers[reg];
      }
      this.deviceData.registers = Object.entries(neededRegisters);
      if (this.deviceData.registers.length) {
        this.deviceData.logging_period = this.deviceData.registers[4][1];
      }
      this.deviceDataChange.emit(this.deviceData);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deviceData) {
      this.deviceData = changes.deviceData.currentValue;
    }
  }

  fetchRegisters() {
    const index = this.item.getIndex();
    if (+index > -1) {
      this.requestMeterRegisterReading();
    }
  }

  secondsToDhms(seconds) {
    seconds = Number(seconds);
    let d = Math.floor(seconds / (3600*24));
    let h = Math.floor(seconds % (3600*24) / 3600);
    let m = Math.floor(seconds % 3600 / 60);
    let s = Math.floor(seconds % 60);

    let dDisplay = d > 0 ? d + "d " : "";
    let hDisplay = h > 0 ? h + "h " : "";
    let mDisplay = m > 0 ? m + "m " : "";
    let sDisplay = s > 0 ? s + "s " : "";
    return dDisplay + hDisplay + mDisplay + sDisplay;
  }

  readUptimes(res) {
    const uptimeParts = Object.values(res);
    let seconds = +uptimeParts[1] * this.MAX_UINT + +uptimeParts[0];
    let secondsOS = (+uptimeParts[3] * this.MAX_UINT) + +uptimeParts[2];
    let uptime = this.secondsToDhms(seconds);
    let uptimeOS = this.secondsToDhms(secondsOS);
    this.deviceData.uptime = uptime;
    this.deviceData.uptimeos = uptimeOS;
    this.deviceDataChange.emit(this.deviceData);
  }

  onUptimeClick() {
    const data = {
      deviceid: this.item.id,
      ip: this.item.getIP(),
      port: DataLoggerDefaults.port,
      device: DataLoggerDefaults.device,
      type: 'uptime'
    };
    this.sendQuery(data);
  }

  onClickMeterIdentityBtn() {
    if (!this.item.parent) {
      return this.service.notiService.open("Meter device does not have any datalogger parent");
    }
    const data = {
      deviceid: this.item.id,
      ip: this.item.parent.getIP(),
      port: this.item.getPort(),
      device: this.item.getAddress(),
      type: 'serial'
    }
    this.sendQuery(data);
  }

  onClickCheckCTBtn() {
    const data = {
      deviceid: this.item.id,
      ip: this.item.parent.getIP(),
      port: this.item.getPort(),
      device: this.item.getAddress(),
      type: 'ct_ratio'
    }
    this.sendQuery(data);
  }

  onClickCheckPrimerBtn() {
    const data = {
      deviceid: this.item.id,
      ip: this.item.parent.getIP(),
      port: this.item.getPort(),
      device: this.item.getAddress(),
      type: 'ct_primer'
    }
    this.sendQuery(data);
  }

  onClickCheckSeconderBtn() {
    const data = {
      deviceid: this.item.id,
      ip: this.item.parent.getIP(),
      port: this.item.getPort(),
      device: this.item.getAddress(),
      type: 'ct_seconder'
    }
    this.sendQuery(data);
  }

  onClickPingBtn() {
    const data = {
      deviceid: this.item.id,
      ip: this.item.getIP()
    }
    this.sendPing(data)
  }

  sendPing(data) {
    if (data.ip) {
      this.service.ping(data).subscribe((resp:any) => {
        if (resp.error) {
          return this.service.notiService.open(resp.error);
        }
        this.deviceData.latecy = resp.latecy ? resp.latecy : 'N/A';
        this.item.setOnline(!!resp.latecy);
        this.deviceDataChange.emit(this.deviceData);
      })
    } else {
      this.service.notiService.open("Can not find IP in device attributes");
    }
  }

  requestMeterRegisterReading() {
    if (!this.item.parent) {
      return null;
    }
    let data: any = {
      device: DataLoggerDefaults.device,
      port: DataLoggerDefaults.port,
      deviceid: this.item.id,
      ip: this.item.parent.getIP(),
      type: 'meter_registers',
      index: this.item.getIndex()
    }
    this.sendQuery(data);
  }

  writeAllRegister() {
    const registerTypes = ['address', 'baud', 'databit', 'stopbit', 'freq', 'parity'];
    let writeList = [];
    let data: any = {};
    registerTypes.forEach(type => {
      data = this.getValueAndRegisterByType(type);
      if (data.value) {
        writeList.push({register: data.register, value: data.value, id: 'meter_register'});
      }
    })
    if (writeList.length > 0) {
      this.service.confirmAction(`Do you want to write these registers? ${JSON.stringify(writeList)}`).subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.handleWriteRegisters(writeList);
        }
      })
    }
  }

  handleWriteRegisters(list) {
    this.service.writeMultiple({deviceid: this.item.parent.id, ip: this.item.parent.getIP(), list: list}).subscribe((resp:any) => {
      if (!resp.success) {
        this.service.notiService.open("Something went wrong, check console log.");
        console.log(resp);
      } else {
        this.service.notiService.open("Successful writing");
      }
    })
  }

  writeMeterRegister(type) {
    const data: any = this.getValueAndRegisterByType(type);
    if (data.value !== null) {
      this.service.confirmAction(`Do you want to write this register? ${data.register}: ${data.value}`).subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.writeRegister(data.register, data.value);
        }
      })
    }
  }

  getValueAndRegisterByType(type) {
    const index = this.item.getIndex();
    if (index === null) {
      return this.service.notiService.open("Missing device index for register writing.");
    }
    let value = null;
    let register = null;
    switch (type) {
      case 'address':
        value = this.item.getAddress();
        register = (+index * 100) + MeterRegisters.address;
        break;
      case 'baud':
        value = +this.item.getBaudRate() / 100;  // 19200 -> 192
        register = (+index * 100) + MeterRegisters.baud;
        break;
      case 'databit':
        value = this.item.getDatabits();
        register = (+index * 100) + MeterRegisters.databit;
        break;
      case 'stopbit':
        value = this.item.getStopbits();
        register = (+index * 100) + MeterRegisters.stopbit;
        break;
      case 'freq':
        value = this.item.getReadFreq();
        register = (+index * 100) + MeterRegisters.readfreq;
        break;
      case 'parity':
        register = (+index * 100) + MeterRegisters.parity;
        let parity = this.item.getParity();
        if (parity === 'EVEN') {
          value = 2;
        } else if (parity === 'ODD') {
          value = 1;
        } else {
          value = 0;
        }
        break;
      default:
        console.log("Missing type from write register", type);
    }
    return {register: register, value: value};
  }

  writeRegister(register, value) {
    this.service.write(this.item.parent.getIP(), register, value, this.item.parent.id).subscribe((resp:any) => {
      if (resp.error) {
        return this.service.notiService.open(resp.error);
      }
      this.service.notiService.open("Successful writing");
      console.log("write res", resp);
    })
  }

  sendQuery(info) {
    this.service.query(info).subscribe((resp:any) => {
      if (resp.error) {
        return this.service.notiService.open(resp.error);
      }
      this.extractQueryResult(resp, info.type)
    })
  }

  saveAttribute(attributeID, value, prop) {
    this.service.saveAttribute({device: this.item.id, attribute: attributeID, value: value}).subscribe((resp:any) => {
      if (resp.success) {
        this.service.notiService.open("Saved");
        this.deviceData[prop] = value;
        this.item.setInfo(attributeID, value);
        this.deviceDataChange.emit(this.deviceData);
      }
    })
  }

  compareResultWithAttribute(res, attributeValue, attributeID, prop) {
    let queryData = this.getFirstObjectValue(res);
    if (!queryData) {
      return this.service.notiService.open("Can not found CT register value.");
    }
    if (queryData != attributeValue) {
      this.service.confirmAction('Different CT found. Device attribute: ' + attributeValue + '. Query result: ' + queryData + '. Do you want to overwrite the device attribute with the query result?', 'Different result')
        .subscribe(isConfirm => {
          if (isConfirm) {
            this.saveAttribute(attributeID, queryData, prop)
          }
        })
    } else {
      this.service.notiService.open("Attribute matches register value.");
    }
  }

  handleSerialResponse(res) {
    let serial = this.item.getSerialNumber();
    let querySerial = this.getFirstObjectValue(res);
    if (!querySerial) {
      return this.service.notiService.open("Can not found Serial register value.");
    }
    if (serial != querySerial) {
      this.service.confirmAction('Different serial number found. Device attribute: ' + serial + '. Query result: ' + querySerial + '. Do you want to overwrite the device attribute with the query result?', 'Different result')
        .subscribe(isConfirm => {
          if (isConfirm) {
            this.saveAttribute(AttributeID.serialNumber, querySerial, 'serial')
          }
        })
    } else {
      this.service.notiService.open("Serial number matches register value.");
    }
  }

  getFirstObjectValue(obj) {
    const keys = Object.keys(obj);
    if (!keys || !keys.length) {
      return null;
    }
    return obj[keys[0]];
  }

  extractQueryResult(res, type) {
    if (Object.keys(res).length) {
      this.item.setLastCom(moment().format("YYYY-MM-DD HH:mm:ss"));
    }
    switch (type) {
      case 'serial':
        this.handleSerialResponse(res);
        break;
      case 'uptime':
        this.readUptimes(res)
        break;
      case 'ct_primer':
        this.compareResultWithAttribute(res, this.item.getPrimary(), AttributeID.ct_primary, 'primary')
        break;
      case 'ct_seconder':
        this.compareResultWithAttribute(res, this.item.getSecondary(), AttributeID.ct_secondary, 'secondary')
        break;
      case 'ct_ratio':
        this.deviceData.primary = res[Register.current_primary];
        this.deviceData.secondary = res[Register.current_seconder];
        this.deviceDataChange.emit(this.deviceData);
        break;
      case 'meter_registers':
        this.deviceData.registers = Object.entries(res);
        if (this.deviceData.registers.length) {
          this.deviceData.logging_period = this.deviceData.registers[4][1];
        }
        this.deviceDataChange.emit(this.deviceData);
        break;
      default:
        console.log("undefined type handler", type);
    }
  }

}
